import { Button, Col, Form, Row } from 'react-bootstrap'
import { FaPlus, FaSave, FaTimes } from 'react-icons/fa'
import { useEffect, useRef } from 'react'
import Select from 'react-select'

const MenuCostForm = ({
    handleAddCostItem,
    formData,
    handleMenuItemSelect,
    allCostItemNames,
    allMenuItemNames,
    handleClearMenuItem,
    handleInputChange,
    handleSubRecipeInputChange,
    handleSubRecipeSelect,
    handleAddSubRecipe,
    handleCancelEdit,
    handleCancelSubRecipeEdit,
    handleCostItemSelect,
    showConversionOptions,
    showSubRecipeConversionOptions,
    showSubRecipe,
    setShowSubRecipe,
    handleUomChange,
    editingIndex,
    kgVariations,
    literVariations,
    gramVariations,
    milliliterVariations,
    subRecipes,
    subRecipeFormData,
    editingSubRecipeIndex,
    menuItems,
    setAutoFill,
    autoFill,
    unitDefinitions
}) => {
    const costItemRef = useRef(null);
    const uomRef = useRef(null);
    const quantityRef = useRef(null);
    const subRecipeRef = useRef(null);
    const subRecipeQuantityRef = useRef(null);
    const subRecipeUomRef = useRef(null);
    const menuItemRef = useRef(null);
    const storedItem = sessionStorage.getItem('item');

    const menuItemOptions = allMenuItemNames.map(name => ({ value: name, label: name }));
    const costItemOptions = allCostItemNames.map(name => ({ value: name, label: name }));
    const subRecipeOptions = subRecipes.map(recipe => ({
        value: recipe.id.toString(),
        label: recipe.name
    }));

    useEffect(() => {
        if (storedItem) {
            setAutoFill(true);
        }

        if (storedItem && !formData.menuItem && autoFill) {
            const selectedItem = menuItems
                .flatMap(type => type.items.flatMap(category => category.items))
                .find(item => item.Name === storedItem);

            if (selectedItem) {
                handleMenuItemChange({ value: selectedItem.Name, label: selectedItem.Name });
            }
        }
    }, [formData.menuItem, menuItems]);

    const getUomOptionsWithDefault = (uom, currentNewUom) => {
        const options = [];
        const lowerUom = uom?.toLowerCase();
        let defaultOption = currentNewUom || null;

        if (kgVariations.includes(lowerUom) || gramVariations.includes(lowerUom)) {
            options.push(
                { value: 'gm', label: 'gm' },
                { value: 'kg', label: 'kg' }
            );
            if (!defaultOption) defaultOption = 'gm';
        } else if (literVariations.includes(lowerUom) || milliliterVariations.includes(lowerUom)) {
            options.push(
                { value: 'ml', label: 'ml' },
                { value: 'ltr', label: 'ltr' }
            );
            if (!defaultOption) defaultOption = 'ml';
        }

        return { options, defaultOption };
    };

    const { options: uomOptions, defaultOption: defaultUom } = getUomOptionsWithDefault(formData.uom, formData.new_uom);
    const { options: subRecipeUomOptions, defaultOption: defaultSubRecipeUom } = getUomOptionsWithDefault(subRecipeFormData.uom, subRecipeFormData.new_uom);

    useEffect(() => {
        if (uomOptions.length > 0 && !formData.new_uom) {
            handleUomChange({
                target: {
                    value: defaultUom,
                    name: "new_uom"
                }
            });
        }
    }, [uomOptions, formData.new_uom]);

    useEffect(() => {
        if (subRecipeUomOptions.length > 0 && !subRecipeFormData.new_uom) {
            handleSubRecipeInputChange({
                target: {
                    value: defaultSubRecipeUom,
                    name: "new_uom"
                }
            });
        }
    }, [subRecipeUomOptions, subRecipeFormData.new_uom]);

    useEffect(() => {
        if (!formData.menuItem && menuItemRef.current) {
            menuItemRef.current.focus();
        } else if (formData.menuItem && !showSubRecipe && costItemRef.current) {
            costItemRef.current.focus();
        } else if (formData.menuItem && showSubRecipe && subRecipeRef.current) {
            subRecipeRef.current.focus();
        }
    }, [formData.menuItem, showSubRecipe]);

    const handleMenuItemKeyDown = (e) => {
        if (e.key === 'Enter' && formData.menuItem) {
            e.preventDefault();
            if (showSubRecipe && subRecipeRef.current) {
                subRecipeRef.current.focus();
            } else if (costItemRef.current) {
                costItemRef.current.focus();
            }
        }
    };

    const handleCostItemKeyDown = (e) => {
        if (e.key === 'Enter' && formData.costItem) {
            e.preventDefault();
            if (quantityRef.current) {
                quantityRef.current.focus();
            }
        }
    };

    const handleQuantityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (showConversionOptions && uomRef.current) {
                uomRef.current.focus();
            } else {
                handleAddCostItem(e);
                if (costItemRef.current) {
                    costItemRef.current.focus();
                }
            }
        }
    };

    const handleUomKeyDown = (e) => {
        if (e.key === 'Enter' && formData.new_uom) {
            e.preventDefault();
            handleAddCostItem(e);
            if (costItemRef.current) {
                costItemRef.current.focus();
            }
        }
    };

    const handleSubRecipeKeyDown = (e) => {
        if (e.key === 'Enter' && subRecipeFormData.subRecipe) {
            e.preventDefault();
            if (subRecipeQuantityRef.current) {
                subRecipeQuantityRef.current.focus();
            }
        }
    };

    const handleSubRecipeQuantityKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (subRecipeUomOptions.length > 0 && subRecipeUomRef.current) {
                subRecipeUomRef.current.focus();
            } else {
                handleAddSubRecipe(e);
                if (subRecipeRef.current) {
                    subRecipeRef.current.focus();
                }
            }
        }
    };

    const handleSubRecipeUomKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSubRecipe(e);
            if (subRecipeRef.current) {
                subRecipeRef.current.focus();
            }
        }
    };

    const handleMenuItemChange = (selectedOption) => {
        if (!selectedOption) return;

        const selectedItem = menuItems
            .flatMap(type => type.items.flatMap(category => category.items))
            .find(item => item.Name === selectedOption.value);

        if (selectedItem) {
            handleMenuItemSelect({
                target: {
                    value: selectedItem.Name,
                    name: "menuItem"
                }
            });
        }
    };

    const handleCostItemChange = (selectedOption) => {
        if (!selectedOption) return;

        const selectedItem = unitDefinitions.find(item => item.name === selectedOption.value);

        if (selectedItem) {
            handleCostItemSelect({
                target: {
                    value: selectedItem.name,
                    name: "costItem"
                }
            });

            if (quantityRef.current) {
                setTimeout(() => {
                    quantityRef.current.focus();
                }, 100);
            }
        }
    };

    const handleUomSelectChange = (selectedOption) => {
        if (!selectedOption) return;

        handleUomChange({
            target: {
                value: selectedOption.value,
                name: "new_uom"
            }
        });
    };

    const handleSubRecipeChange = (selectedOption) => {
        if (!selectedOption) return;

        const syntheticEvent = {
            target: {
                value: selectedOption.value.toString(),
                name: "subRecipe"
            }
        };

        handleSubRecipeSelect(syntheticEvent);

        if (subRecipeQuantityRef.current) {
            setTimeout(() => {
                subRecipeQuantityRef.current.focus();
            }, 100);
        }
    };

    const handleSubRecipeUomChange = (selectedOption) => {
        if (!selectedOption) return;

        handleSubRecipeInputChange({
            target: {
                value: selectedOption.value,
                name: "new_uom"
            }
        });
    };

    return (
        <>
            <Form onSubmit={handleAddCostItem} className="mb-8 bg-gray-50 p-4 rounded-lg">
                <Row className="mb-4">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Menu Item</Form.Label>
                            <div className="flex gap-2">
                                <div className='w-full'>
                                    <Select
                                        ref={menuItemRef}
                                        options={menuItemOptions}
                                        value={formData.menuItem ? { value: formData.menuItem, label: formData.menuItem } : null}
                                        onChange={handleMenuItemChange}
                                        placeholder="Select a menu item"
                                        isDisabled={!!formData.menuItem}
                                        className="basic-single"
                                        classNamePrefix="select"
                                        menuPortalTarget={document.body}
                                        onKeyDown={handleMenuItemKeyDown}
                                    />
                                </div>
                                {formData.menuItem && (
                                    <Button variant="danger" onClick={handleClearMenuItem}>
                                        <FaTimes />
                                    </Button>
                                )}
                            </div>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Selling Price</Form.Label>
                            <Form.Control
                                type="text"
                                name="sellingPrice"
                                value={formData.sellingPrice}
                                onChange={handleInputChange}
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3} className="flex items-end">
                        {formData.menuItem && (
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={showSubRecipe}
                                    onChange={() => setShowSubRecipe(!showSubRecipe)}
                                />
                                Add Sub-Recipe
                            </label>
                        )}
                    </Col>
                </Row>

                {!showSubRecipe && formData.menuItem && (
                    <>
                        <Row className="mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Ingredients</Form.Label>
                                    <Select
                                        ref={costItemRef}
                                        options={costItemOptions}
                                        value={formData.costItem ? { value: formData.costItem, label: formData.costItem } : null}
                                        onChange={handleCostItemChange}
                                        placeholder="Select an ingredient"
                                        required
                                        isSearchable
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        onKeyDown={handleCostItemKeyDown}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Rate</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="rate"
                                        value={formData.rate}
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Unit</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="unit"
                                        value={
                                            formData.unit
                                                ? `${formData.unit} (${formData.uom})`
                                                : ''
                                        }
                                        readOnly
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        required
                                        step="any"
                                        min="0"
                                        ref={quantityRef}
                                        onKeyDown={handleQuantityKeyDown}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label>UOM</Form.Label>
                                    {showConversionOptions && uomOptions.length > 0 ? (
                                        <Select
                                            ref={uomRef}
                                            options={uomOptions}
                                            value={formData.new_uom ?
                                                uomOptions.find(option => option.value === formData.new_uom) :
                                                (uomOptions.length > 0 ? uomOptions[0] : null)}
                                            onChange={handleUomSelectChange}
                                            placeholder="Select UOM"
                                            className="basic-single"
                                            classNamePrefix="select"
                                            menuPortalTarget={document.body}
                                            onKeyDown={handleUomKeyDown}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            name="uom"
                                            value={formData.uom}
                                            readOnly
                                        />
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="flex gap-2">
                            <Button variant="primary" type="submit">
                                <div className='flex justify-center items-center gap-2'>
                                    {editingIndex !== null ? <FaSave /> : <FaPlus />}
                                    {editingIndex !== null ? 'Update' : 'Add'}
                                </div>
                            </Button>
                            {editingIndex !== null && (
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    <div className='flex justify-center items-center gap-2'>
                                        <FaTimes />
                                        Cancel
                                    </div>
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </Form>

            {showSubRecipe && formData.menuItem && (
                <Form onSubmit={handleAddSubRecipe} className="mb-8 bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">Add Sub-Recipe</h3>
                    <Row className="mb-4">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Sub-Recipe</Form.Label>
                                <Select
                                    ref={subRecipeRef}
                                    options={subRecipeOptions}
                                    value={subRecipeFormData.subRecipe ?
                                        subRecipeOptions.find(option => option.value === subRecipeFormData.subRecipe.toString()) :
                                        null
                                    }
                                    onChange={handleSubRecipeChange}
                                    placeholder="Select a sub-recipe"
                                    className="basic-single"
                                    classNamePrefix="select"
                                    menuPortalTarget={document.body}
                                    onKeyDown={handleSubRecipeKeyDown}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Rate</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="rate"
                                    value={subRecipeFormData.rate}
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Unit</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="unit"
                                    value={
                                        subRecipeFormData.unit
                                            ? `${subRecipeFormData.unit} (${subRecipeFormData.uom})`
                                            : ''
                                    }
                                    readOnly
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="quantity"
                                    value={subRecipeFormData.quantity}
                                    onChange={handleSubRecipeInputChange}
                                    required
                                    step="any"
                                    min="0"
                                    ref={subRecipeQuantityRef}
                                    onKeyDown={handleSubRecipeQuantityKeyDown}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>UOM</Form.Label>
                                {showSubRecipeConversionOptions && subRecipeUomOptions.length > 0 ? (
                                    <Select
                                        ref={subRecipeUomRef}
                                        options={subRecipeUomOptions}
                                        value={subRecipeFormData.new_uom ?
                                            subRecipeUomOptions.find(option => option.value === subRecipeFormData.new_uom) :
                                            (subRecipeUomOptions.length > 0 ? subRecipeUomOptions[0] : null)}
                                        onChange={handleSubRecipeUomChange}
                                        placeholder="Select UOM"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                        }}
                                        onKeyDown={handleSubRecipeUomKeyDown}
                                        isSearchable={false}
                                    />
                                ) : (
                                    <Form.Control
                                        type="text"
                                        name="uom"
                                        value={subRecipeFormData.uom}
                                        readOnly
                                        onKeyDown={handleSubRecipeUomKeyDown}
                                    />
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="flex gap-2">
                        <Button variant="primary" type="submit">
                            <div className='flex justify-center items-center gap-2'>
                                {editingSubRecipeIndex !== null ? <FaSave /> : <FaPlus />}
                                {editingSubRecipeIndex !== null ? 'Update' : 'Add'}
                            </div>
                        </Button>
                        {editingSubRecipeIndex !== null && (
                            <Button variant="secondary" onClick={handleCancelSubRecipeEdit}>
                                <div className='flex justify-center items-center gap-2'>
                                    <FaTimes />
                                    Cancel
                                </div>
                            </Button>
                        )}
                    </div>
                </Form>
            )}
        </>
    )
}

export default MenuCostForm